import { PAYMENT_UNAVAILABLE_COPY } from './constants';

export function isApiError(
  error: unknown
): error is { response?: { data?: { message?: string }; status?: number } } {
  return typeof error === 'object' && error !== null && 'response' in error;
}

export function getErrorMessage(error: unknown, fallback: string): string {
  const code = getErrorCode(error);

  if (code === 'PROVIDER_NOT_CONFIGURED') return PAYMENT_UNAVAILABLE_COPY;

  // The backend only guarantees customer-safe wording on a coded 4xx it shaped.
  // A 5xx carries either the fixed "Internal Server Error" or internal
  // invariant text; a refusal with no code is written for an operator; and
  // anything the adapter never touched is a raw JS throw whose message is a
  // stack detail. That last case is why the test is `_httpHandled` and not the
  // name string: `code === 'Error'` matched the base class only, so a native
  // TypeError from inside a guarded block reached the customer verbatim.
  if (!isHandledError(error) || code === 'Error' || isServerError(error)) {
    return fallback;
  }

  if (isApiError(error)) {
    return error.response?.data?.message ?? fallback;
  }
  return error instanceof Error && error.message ? error.message : fallback;
}

// The adapter throws a bare Error with the status copied onto it and no
// `response`, so reading `response.status` alone never matches a real error.
export function getErrorStatus(error: unknown): number | undefined {
  return (
    (error as { status?: number } | null)?.status ??
    (isApiError(error) ? error.response?.status : undefined)
  );
}

export function isHttpStatus(error: unknown, status: number): boolean {
  return getErrorStatus(error) === status;
}

// An unhandled 5xx comes back as a fixed "Internal Server Error", and the few
// deliberate 5xx carry internal invariant text; neither is worth showing a
// customer.
export function isServerError(error: unknown): boolean {
  return (getErrorStatus(error) ?? 0) >= 500;
}

// The HTTP adapter stashes the backend's error code on Error.name.
export function getErrorCode(error: unknown): string | undefined {
  return (error as { name?: string } | null)?.name;
}

// Whatever the backend sent as `details`, so every reader validates it.
export function getErrorDetails(error: unknown): unknown {
  return (error as { details?: unknown } | null)?.details;
}

// Every error a service can throw passes through toHandledError, which stamps
// this. Its absence means the throw came from our own code, not the wire.
export const isHandledError = (error: unknown): boolean =>
  !!(error as { _httpHandled?: boolean } | null)?._httpHandled;

// Distinct from `_httpHandled`: an ignored code, an ignored status or an auth
// endpoint is handled by the adapter and deliberately never reported, so a
// caller adding its own context still owns that report. This says the report
// already exists, and captureError drops anything it is stamped on.
export const wasCaptured = (error: unknown): boolean =>
  !!(error as { _httpCaptured?: boolean } | null)?._httpCaptured;
